/**
 * Writes confirmed medications / allergies back to the patient's chart.
 *
 * Retargeted for HealthDesk. The source project's two paths happened to be this
 * app's shape already — `users/{uid}/medications/*` and
 * `users/{uid}.healthRecords.allergies` — so the PATHS are unchanged. The
 * medication WRITE BEHAVIOUR is not.
 *
 * Called by VisitStartMedicationsElement / VisitStartAllergiesElement only when
 * the shell passes canWriteCmr === true.
 *
 * Changed from the source: dropped the staff_proxy branch, which called a
 * `writeStaffProxyCmr` callable that Tier 1 does not port.
 */

import { db } from "services/firebase";
import { medicationService } from "services/medicationService";

/** Match key for reconciliation. Name is the only field a patient reliably restates. */
const medKey = (row) =>
  String(row?.name || row?.medicationName || "")
    .trim()
    .toLowerCase();

/**
 * Drop empty optionals instead of writing "", matching MedicationForm's
 * convention. Not for name, dosage or frequency — see the payload below.
 */
const withoutEmpty = (obj) =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== "" && v != null));

/**
 * Reconcile the confirmed list against what is on file, matched on medication
 * name. The patient has just reviewed the whole list, so the list they
 * confirmed is the list of record — but this is deliberately NOT the source's
 * delete-all-then-re-add.
 *
 * Medication doc ids are foreign keys in this app:
 *   users/{uid}/medicationReminders/{medicationId}   — doc id IS the medication id
 *   users/{uid}/medicationTracking/{date}.medications — map keyed by medication id
 *   adherenceRecords.medicationId                    — top-level collection
 *
 * Replace-all mints new ids and silently orphans all three, so an unchanged
 * medication must keep its id. Deletions go through
 * medicationService.deleteMedication, which purges the matching reminders.
 *
 * @param {{ patientUid: string, medications: Array<{name,dosage,frequency}> }} args
 * @returns {Promise<{ appliedFields: string[] }>}
 */
export async function writeVisitStartMedications({
  patientUid,
  medications,
} = {}) {
  const uid = String(patientUid || "").trim();
  const list = Array.isArray(medications) ? medications : [];
  if (!uid) throw new Error("patientUid is required.");

  const existing = await medicationService.getMedications(uid);
  const existingByKey = new Map();
  existing.forEach((row) => {
    const key = medKey(row);
    // First wins: getMedications is ordered createdAt desc, so on a duplicate
    // name the newer doc is the one kept and the older one is treated as removed.
    if (key && !existingByKey.has(key)) existingByKey.set(key, row);
  });

  const matchedIds = new Set();

  for (const med of list) {
    const key = medKey(med);
    if (!key) continue;
    // name, dosage and frequency are always sent, even blank. firestore.rules
    // requires all three KEYS on a medication doc (isValidMedication) while
    // allowing dosage and frequency to be empty strings, so dropping a blank one
    // is rejected as a permissions error rather than a validation one. The
    // check-in is the only place they can be blank: it stopped requiring a dosage
    // the patient does not know.
    const payload = {
      name: String(med.name || "").trim(),
      dosage: String(med.dosage || "").trim(),
      frequency: String(med.frequency || "").trim(),
      ...withoutEmpty({
        prescribedBy: String(med.prescribedBy || med.prescriber || "").trim(),
        notes: String(med.notes || "").trim(),
        // The verified-search id: what the chart's "Verified" badge, dose check
        // and label lookup (drugReference) key on. Dropping it here made every
        // check-in medication land on the chart as hand-entered.
        rxcui: String(med.rxcui || "").trim(),
        source: "visit-start",
      }),
    };

    const match = existingByKey.get(key);
    if (match) {
      matchedIds.add(match.id);
      await medicationService.updateMedication(uid, match.id, payload);
    } else {
      await medicationService.createMedication(uid, payload);
    }
  }

  for (const row of existing) {
    if (matchedIds.has(row.id)) continue;
    await medicationService.deleteMedication(uid, row.id);
  }

  return { appliedFields: ["medications"] };
}

/**
 * @param {{ patientUid: string, allergies: Array<{allergen,type,reaction,notes}> }} args
 * @returns {Promise<{ appliedFields: string[] }>}
 */
export async function writeVisitStartAllergies({ patientUid, allergies } = {}) {
  const uid = String(patientUid || "").trim();
  const list = Array.isArray(allergies) ? allergies : [];
  if (!uid) throw new Error("patientUid is required.");

  // Dotted paths on update() write nested healthRecords.* without replacing
  // sibling fields. Do NOT use set({ merge: true }) with dotted keys — that
  // creates a literal top-level field called "healthRecords.allergies" — and do
  // not pass a nested healthRecords object, which replaces the whole map.
  //
  // This is deliberately narrower than AllergiesPage, which read-modify-writes
  // the whole healthRecords map from state captured at mount and can therefore
  // clobber pmh / psh / other if they changed in another tab.
  //
  // updatedAt is an ISO string, not serverTimestamp(): AllergiesPage and
  // scripts/seedPatientRecord.js both read this field as a string.
  await db
    .collection("users")
    .doc(uid)
    .update({
      "healthRecords.allergies": list,
      "healthRecords.updatedAt": new Date().toISOString(),
    });
  return { appliedFields: ["allergies"] };
}

/**
 * Fills in date of birth and sex when the chart was missing them.
 *
 * FILL-IN-THE-BLANKS, not correct-what-is-there, and that is a property of the
 * question rather than a limitation here. VisitStartIdentityElement only ASKS for
 * these two when the record does not already have them; when it does, it shows
 * them back disabled with a link to the page that owns them, deliberately (see
 * that file's header on why an editable field that discards the edit is worse
 * than no field). So there is never a changed value to write back, only a gap to
 * close — and closing it is worth doing, because until now the patient typed
 * their date of birth into the check-in and it went nowhere.
 *
 * Each field is written only if it has a value AND the chart lacked it, so a
 * concurrent edit elsewhere cannot be overwritten by a stale intake answer.
 *
 * @param {{ patientUid: string, dateOfBirth?: string, sex?: string }} args
 * @returns {Promise<{ appliedFields: string[] }>}
 */
export async function writeVisitStartDemographics({
  patientUid,
  dateOfBirth,
  sex,
} = {}) {
  const uid = String(patientUid || "").trim();
  if (!uid) throw new Error("patientUid is required.");

  const snap = await db.collection("users").doc(uid).get();
  const existing = (snap.exists && snap.data()?.healthRecords) || {};

  const patch = {};
  const appliedFields = [];
  const dob = String(dateOfBirth || "").trim();
  // Lowercase to match what DemographicsPage's Sex select stores.
  const sexValue = String(sex || "").trim().toLowerCase();

  if (dob && !existing.dateOfBirth) {
    patch["healthRecords.dateOfBirth"] = dob;
    appliedFields.push("dateOfBirth");
  }
  if (sexValue && !existing.sex) {
    patch["healthRecords.sex"] = sexValue;
    appliedFields.push("sex");
  }
  if (!appliedFields.length) return { appliedFields: [] };

  // Dotted paths, for the reason spelled out in writeVisitStartAllergies above.
  patch["healthRecords.updatedAt"] = new Date().toISOString();
  await db.collection("users").doc(uid).update(patch);
  return { appliedFields };
}

/**
 * Replay a finished check-in onto a chart that did not exist while it was being
 * answered — the guest who creates an account on the completion screen.
 *
 * A guest runs the whole loop with `canWriteCmr === false`, so none of the three
 * writers above ever fired and their answers live only on the session document.
 * This is that same set of writes, run once, from the session's stored answers
 * rather than from an element's in-memory state. Nothing new is written and no
 * new mapping is invented: the three functions above stay the only code that
 * knows where a medication or an allergy belongs.
 *
 * PER-SECTION BEST EFFORT, on purpose. The account has already been created by
 * the time this runs, so throwing on the first failure would leave a patient with
 * an account, no explanation, and two of three sections seeded — and a retry that
 * starts by trying to create the account again. Each section is therefore
 * independent and the caller is told which ones did not land.
 *
 * Only the three chart-shaped sections are replayed. The chief complaint, the ROS
 * answers and the referral question are facts about ONE visit, not standing chart
 * data, and they stay on the session where the clinician's code reads them.
 *
 * @param {{ patientUid: string, elementAnswers?: object }} args
 * @returns {Promise<{ appliedFields: string[], failedFields: string[] }>}
 */
export async function seedCmrFromVisitStartIntake({
  patientUid,
  elementAnswers = {},
} = {}) {
  const uid = String(patientUid || "").trim();
  if (!uid) throw new Error("patientUid is required.");

  const answers = elementAnswers || {};
  const identity = answers.identity || {};
  const allergies = answers.allergies?.allergies;
  const medications = answers["medication-list"]?.medications;

  const sections = [
    {
      // Not gated on the chart already having them, unlike the identity element's
      // own call: a chart created seconds ago cannot have them.
      field: "demographics",
      skip: !identity.dateOfBirth && !identity.sex,
      run: () =>
        writeVisitStartDemographics({
          patientUid: uid,
          dateOfBirth: identity.dateOfBirth,
          sex: identity.sex,
        }),
    },
    {
      // An empty array is a real answer here — "no known drug allergies" is
      // stored as a row, so a list that arrived empty means the element never
      // ran, not that the patient has none.
      field: "allergies",
      skip: !Array.isArray(allergies) || !allergies.length,
      run: () => writeVisitStartAllergies({ patientUid: uid, allergies }),
    },
    {
      field: "medications",
      skip: !Array.isArray(medications) || !medications.length,
      run: () => writeVisitStartMedications({ patientUid: uid, medications }),
    },
  ];

  const appliedFields = [];
  const failedFields = [];

  for (const section of sections) {
    if (section.skip) continue;
    try {
      const result = await section.run();
      appliedFields.push(...(result?.appliedFields || [section.field]));
    } catch (err) {
      console.warn(
        `[VisitStart] seeding ${section.field} onto the new chart failed`,
        err,
      );
      failedFields.push(section.field);
    }
  }

  return { appliedFields, failedFields };
}
